import { STATUSES, SUBJECT_TYPES } from './store/constants';

import type {
	Category,
	Duration,
	GoalSettingsPayload,
	GoalSettingsPayloadContent,
	Subcategory,
	Subject,
} from './types';

export const setSelectedDuration = (
	selectedDuration: Duration,
	durations: Duration[],
): Duration[] => {
	return durations.map((duration) => {
		duration.is_selected = duration.id === selectedDuration.id;
		return duration;
	});
};

export const setSelectedSubject = (
	subjectId: number,
	selected: boolean,
	subjects: Subject[],
): Subject[] => {
	return subjects.map((subject) => {
		if (subject.id === subjectId) {
			subject.selected = selected;
		}
		return subject;
	});
};

export const getSubjectTopicSubcategories = (subject: Subject): Subcategory[] => {
	const visibleCategories =
		subject.topic_categories?.filter((category) => category.status !== STATUSES.HIDDEN) ?? [];

	const visibleSubcategories = visibleCategories.flatMap((category) =>
		(category.topic_subcategories ?? []).filter(
			(subcategory) => subcategory.status !== STATUSES.HIDDEN,
		),
	);

	const selectableSubcategories = visibleSubcategories.filter((subcategory) =>
		[STATUSES.ACTIVE, STATUSES.CHALLENGE].includes(subcategory.status),
	);

	return selectableSubcategories.length ? selectableSubcategories : visibleSubcategories;
};

export const hasSubjectSelectedTopics = (subject: Subject): boolean => {
	return getSubjectTopicSubcategories(subject).some((subcategory) => subcategory.selected);
};

export const isSubjectMissingSelectedTopics = (subject: Subject): boolean => {
	return subject.selected && !hasSubjectSelectedTopics(subject);
};

export const setSelectedCategory = (
	subjectId: number,
	categoryId: number,
	selected: boolean,
	subjects: Subject[],
): Subject[] => {
	return subjects.map((subject) => {
		if (subject.id === subjectId) {
			subject.topic_categories =
				subject.type === SUBJECT_TYPES.practice
					? selectPracticeCategory(categoryId, selected, subject.topic_categories)
					: selectExamCategory(categoryId, selected, subject.topic_categories);
		}
		return subject;
	});
};

export const selectPracticeCategory = (
	categoryId: number,
	selected: boolean,
	categories: Category[],
): Category[] => {
	return categories.map((category) => {
		if (category.id === categoryId && category.status === STATUSES.ACTIVE) {
			category.selected = selected;
			category.partially_selected = false;
			category.topic_subcategories = category.topic_subcategories.map((subcategory) => {
				if (subcategory.status === STATUSES.ACTIVE) {
					subcategory.selected = selected;
				}
				return subcategory;
			});
		}
		return category;
	});
};

export const selectExamCategory = (
	categoryId: number,
	selected: boolean,
	categories: Category[],
): Category[] => {
	return categories.map((category) => {
		const isSelectedCategory = category.id === categoryId;
		category.selected = isSelectedCategory && selected;
		category.partially_selected = false;
		category.topic_subcategories = category.topic_subcategories.map((subcategory) => {
			subcategory.selected = isSelectedCategory && selected;
			return subcategory;
		});
		return category;
	});
};

export const setSelectedSubcategory = (
	subjectId: number,
	categoryId: number,
	subcategoryId: number,
	selected: boolean,
	subjects: Subject[],
): Subject[] => {
	return subjects.map((subject) => {
		if (subject.id === subjectId) {
			if (subject.type === SUBJECT_TYPES.practice) {
				subject.topic_categories = selectPracticeSubcategory(
					categoryId,
					subcategoryId,
					selected,
					subject.topic_categories,
				);
			} else {
				subject.topic_categories = selectExamSubcategory(
					categoryId,
					subcategoryId,
					selected,
					subject.topic_categories,
				);
			}
		}
		return subject;
	});
};

export const selectPracticeSubcategory = (
	categoryId: number,
	subcategoryId: number,
	selected: boolean,
	categories: Category[],
): Category[] => {
	return categories.map((category) => {
		if (category.id === categoryId) {
			let selectedCount = 0;
			category.topic_subcategories = category.topic_subcategories.map((subcategory) => {
				if (
					subcategory.id === subcategoryId &&
					[STATUSES.ACTIVE, STATUSES.CHALLENGE].includes(subcategory.status)
				) {
					subcategory.selected = selected;
				}
				if (subcategory.selected) {
					selectedCount += 1;
				}
				return subcategory;
			});
			if (selectedCount === category.topic_subcategories.length) {
				category.selected = true;
				category.partially_selected = false;
			} else if (selectedCount > 0) {
				category.selected = true;
				category.partially_selected = true;
			} else {
				category.selected = false;
				category.partially_selected = false;
			}
		}
		return category;
	});
};

export const selectExamSubcategory = (
	categoryId: number,
	subcategoryId: number,
	selected: boolean,
	categories: Category[],
): Category[] => {
	return categories.map((category) => {
		const isSelectedCategory = category.id === categoryId;

		if (isSelectedCategory) {
			let selectedCount = 0;
			category.selected = isSelectedCategory;
			category.topic_subcategories = category.topic_subcategories.map((subcategory) => {
				if (subcategory.id === subcategoryId) {
					subcategory.selected = isSelectedCategory && selected;
				}
				if (subcategory.selected) {
					selectedCount += 1;
				}
				return subcategory;
			});
			if (selectedCount === category.topic_subcategories.length) {
				category.selected = true;
				category.partially_selected = false;
			} else if (selectedCount > 0) {
				category.selected = true;
				category.partially_selected = true;
			} else {
				category.selected = false;
				category.partially_selected = false;
			}
		} else {
			category.selected = false;
			category.topic_subcategories = category.topic_subcategories.map((subcategory) => {
				subcategory.selected = false;
				return subcategory;
			});
		}

		return category;
	});
};

export const generateGoalSettingsPayload = (subjects: Subject[]): GoalSettingsPayload => {
	const userGoalContents: GoalSettingsPayloadContent[] = [];
	subjects.forEach((subject) => {
		if (!subject.selected) return;

		const { topic_categories: categories } = subject;
		categories.forEach((category) => {
			const {
				topic_subcategories: subcategories,
				selected,
				partially_selected,
				status,
			} = category;
			if (
				(selected || partially_selected) &&
				[STATUSES.ACTIVE, STATUSES.CHALLENGE].includes(status)
			) {
				subcategories.forEach((subcategory) => {
					if (
						subcategory.selected &&
						[STATUSES.ACTIVE, STATUSES.CHALLENGE].includes(subcategory.status)
					) {
						userGoalContents.push({
							subject_id: subject.id,
							topic_category_id: category.id,
							topic_subcategory_id: subcategory.id,
						});
					}
				});
			}
		});
	});

	return {
		user_goal: {
			user_goal_contents_attributes: userGoalContents,
		},
	};
};

export const setExtraProperties = (subjects: Subject[]): Subject[] => {
	return subjects.map((subject) => {
		subject.topic_categories = subject.topic_categories.map((category) => {
			category.topic_subcategories = category.topic_subcategories.map((subcategory) => {
				subcategory.subjectId = subject.id;
				subcategory.categoryId = category.id;
				return subcategory;
			});
			return category;
		});
		return subject;
	});
};
